import { FileText } from 'lucide-react';

interface DocumentCardProps {
  title: string;
  preview: string;
  wordCount?: number;
  onClick: () => void;
}

export function DocumentCard({ title, preview, wordCount, onClick }: DocumentCardProps) {
  const truncatedPreview = preview && preview.length > 180 ? preview.slice(0, 180) + '…' : preview || '';
  const cleanPreview = truncatedPreview
    .replace(/^#+\s*/gm, '')
    .replace(/\*\*/g, '')
    .replace(/\*/g, '')
    .replace(/^-\s*/gm, '')
    .trim();

  return (
    <div
      data-testid="document-card"
      onClick={onClick}
      style={{
        marginTop: 16,
        border: '0.5px solid var(--color-border-tertiary, #e5e7eb)',
        borderRadius: 'var(--border-radius-lg, 12px)',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'box-shadow 0.15s',
        background: 'var(--color-background-primary, #ffffff)',
        padding: 20,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.boxShadow = 'none';
      }}
    >
      {/* Header row: icon + title + word count */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 7,
            background: 'var(--color-background-secondary, #f3f4f6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            marginTop: 1,
          }}
        >
          <FileText size={15} style={{ color: 'var(--color-text-secondary, #6b7280)' }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            data-testid="text-document-card-title"
            style={{
              fontSize: 15,
              fontWeight: 500,
              color: 'var(--color-text-primary, #111827)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: 1.4,
            }}
          >
            {title}
          </div>
          {wordCount && (
            <span
              style={{
                display: 'inline-block',
                marginTop: 4,
                fontSize: 11,
                color: 'var(--color-text-tertiary, #9ca3af)',
                background: 'var(--color-background-secondary, #f3f4f6)',
                borderRadius: 4,
                padding: '1px 6px',
              }}
            >
              ~{wordCount.toLocaleString()} words
            </span>
          )}
        </div>
      </div>

      {/* Preview excerpt */}
      {cleanPreview && (
        <div
          data-testid="text-document-card-preview"
          style={{
            fontSize: 13,
            color: 'var(--color-text-secondary, #6b7280)',
            lineHeight: 1.55,
            marginBottom: 14,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {cleanPreview}
        </div>
      )}

      {/* Footer CTA */}
      <div
        style={{
          fontSize: 13,
          color: 'var(--color-text-tertiary, #9ca3af)',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        Open Full Report →
      </div>
    </div>
  );
}
